/**
 * Jupiter API Service for token swapping via Shyft proxy
 */
import { Connection, VersionedTransaction, Keypair, LAMPORTS_PER_SOL, PublicKey } from '@solana/web3.js';
import { createClient } from '@supabase/supabase-js';
import { restoreTradingWalletKeypair } from './tradingWalletService';
import { Transaction, SystemProgram } from '@solana/web3.js';
import { sendAndConfirmTransaction } from '@solana/web3.js';
import { getTokenBalance } from './shyftService';
import { getTokenInfo } from './solanatrackerService';
import { env } from '../config/env';
// Explicitly import Buffer with a defensive fallback
import { Buffer as BufferImport } from 'buffer';
import { Decimal } from 'decimal.js';
import { getMint } from '@solana/spl-token';

// Ensure Buffer is available globally and provide fallback
const safeBuffer = (typeof window !== 'undefined' && window.Buffer) ||
    BufferImport ||
    (typeof global !== 'undefined' && global.Buffer);

// Defensive check - if Buffer is not available, create a minimal implementation
if (!safeBuffer) {
    console.error('Buffer not available! Creating fallback implementation.');
}

// SOL token mint address
export const SOL_MINT = 'So11111111111111111111111111111111111111112';

// Jupiter API base URLs via Shyft
const JUPITER_API_BASE = 'https://jup.ams.shyft.to';
const JUPITER_QUOTE_ENDPOINT = '/quote';
const JUPITER_SWAP_ENDPOINT = '/swap';

// Shyft API key from environment
const SHYFT_API_KEY = env.SHYFT_API_KEY;

// Replace with direct Jupiter API URLs
const JUPITER_QUOTE_API = 'https://lite-api.jup.ag/swap/v1/quote';
const JUPITER_SWAP_API = 'https://lite-api.jup.ag/swap/v1/swap';

// Solana connection for transaction sending
// Use the Shyft RPC endpoint with the provided API key
const connection = new Connection(`https://rpc.shyft.to?api_key=${SHYFT_API_KEY}`, 'confirmed');

// Flag to control whether to simulate transactions (can be disabled if RPC has issues)
const SHOULD_SIMULATE_TRANSACTIONS = false;

// SOL has a fixed 9 decimals
export const SOL_DECIMALS = 9;

// Function to check wallet balance
async function checkWalletBalance(walletPublicKey: string): Promise<number> {
    try {
        const publicKey = new PublicKey(walletPublicKey);
        const balance = await connection.getBalance(publicKey);
        return balance;
    } catch (error) {
        throw error;
    }
}

export interface QuoteParams {
    inputMint: string;
    outputMint: string;
    amount: string;
    slippageBps?: string;
}

export interface SwapParams {
    quoteResponse: any;
    userPublicKey: string;
    wrapAndUnwrapSol?: boolean;
    feeAccount?: string;
    platformFeeBps?: string;
    platformFeeAccount?: string;
    computeUnitPriceMicroLamports?: number;
}

/**
 * Converts a human-readable token amount to atomic units based on token decimals
 * @param humanAmount Amount in human-readable format (e.g. 29934.307678784)
 * @param decimals Number of decimal places for the token (e.g. 6 for USDC, 9 for SOL)
 * @returns String representation of the amount in atomic units
 */
export function toAtomicUnits(humanAmount: string | number, decimals: number): string {
    if (typeof decimals !== 'number' || decimals < 0 || !Number.isInteger(decimals)) {
        throw new Error(`Invalid decimals value: ${decimals}. Must be a non-negative integer.`);
    }

    // Use Decimal.js for precise conversion without floating point errors
    return new Decimal(humanAmount).mul(new Decimal(10).pow(decimals)).toFixed(0);
}

/**
 * Converts atomic units back to human-readable token amount
 * @param atomicAmount Amount in atomic units (e.g. 29934307678784)
 * @param decimals Number of decimal places for the token (e.g. 6 for USDC, 9 for SOL)
 * @returns String representation of the amount in human-readable format
 */
export function fromAtomicUnits(atomicAmount: string | number, decimals: number): string {
    if (typeof decimals !== 'number' || decimals < 0 || !Number.isInteger(decimals)) {
        throw new Error(`Invalid decimals value: ${decimals}. Must be a non-negative integer.`);
    }

    // Use Decimal.js for precise conversion without floating point errors
    return new Decimal(atomicAmount).div(new Decimal(10).pow(decimals)).toString();
}

/**
 * Get a quote for swapping tokens
 * @param params Quote parameters
 * @returns Quote response
 */
export async function getJupiterQuote(params: QuoteParams): Promise<any> {
    try {
        console.log(`Getting quote: ${params.inputMint} → ${params.outputMint}, amount: ${params.amount}`);

        // IMPORTANT FIX: Analyze what's happening
        // The issue is that Jupiter's API *appears* to want a human-readable amount but *actually* wants atomic units
        // This is because Jupiter's API has a mismatch in how it processes the input amount

        const amount = params.amount.toString().trim();

        // Construct URL with query parameters
        const queryParams = new URLSearchParams({
            inputMint: params.inputMint,
            outputMint: params.outputMint,
            amount: amount,
        });

        // Add either slippageBps OR dynamicSlippage - cannot use both at the same time
        if (params.slippageBps) {
            // Use static slippage when explicitly provided
            queryParams.append('slippageBps', params.slippageBps);
        } else {
            // Fall back to dynamic slippage when no static slippage is specified
            queryParams.append('dynamicSlippage', 'true');
        }

        // Add recommended parameters for better results
        queryParams.append('restrictIntermediateTokens', 'true');

        const url = `${JUPITER_QUOTE_API}?${queryParams.toString()}`;
        console.log('Quote URL:', url);

        const response = await fetch(url, {
            headers: {
                'Accept': 'application/json',
                'Connection': 'keep-alive'
            }
        });

        console.log('Quote response status:', response.status);

        // Handle 429 Too Many Requests error by falling back to Shyft proxy
        if (response.status === 429) {
            console.log('Received 429 Too Many Requests from Jupiter API, falling back to shyft.ams endpoint');
            // Construct Shyft URL with the same parameters
            const shyftUrl = `${JUPITER_API_BASE}${JUPITER_QUOTE_ENDPOINT}?${queryParams.toString()}`;
            console.log('Fallback Quote URL:', shyftUrl);

            const shyftResponse = await fetch(shyftUrl, {
                headers: {
                    'Accept': 'application/json',
                    'x-api-key': SHYFT_API_KEY
                }
            });

            const shyftResponseText = await shyftResponse.text();

            if (!shyftResponse.ok) {
                throw new Error(`Shyft fallback also failed: ${shyftResponse.status} - ${shyftResponseText}`);
            }

            const shyftQuoteData = JSON.parse(shyftResponseText);
            console.log('Quote obtained successfully from Shyft fallback');
            return shyftQuoteData;
        }

        // Get the response text for better error reporting
        const responseText = await response.text();

        if (!response.ok) {
            // Handle specific error cases with more helpful messages
            if (responseText.includes("missing token decimals")) {
                throw new Error(`TOKEN_METADATA_MISSING: This token is very new and Jupiter is still indexing its metadata. Please try again in a few minutes or use a different DEX.`);
            }

            // If route not found, try again with different parameters
            if (responseText.includes("COULD_NOT_FIND_ANY_ROUTE")) {
                console.log("Could not find route with default parameters, trying alternatives...");

                // Try with different parameters - higher slippage and no restrictions
                const alternativeParams = new URLSearchParams({
                    inputMint: params.inputMint,
                    outputMint: params.outputMint,
                    amount: amount,
                    slippageBps: '5000', // Increase to 50% slippage for low liquidity tokens
                    onlyDirectRoutes: 'false',
                    restrictIntermediateTokens: 'false'
                });

                console.log("Trying with higher slippage (50%) and no restrictions");
                const alternativeResponse = await fetch(`${JUPITER_QUOTE_API}?${alternativeParams.toString()}`);
                const alternativeText = await alternativeResponse.text();

                if (!alternativeResponse.ok) {
                    // If still no route, try a different Jupiter API version
                    if (alternativeText.includes("COULD_NOT_FIND_ANY_ROUTE")) {
                        console.log("Still no route found, trying V6 Jupiter API...");
                        return await getJupiterV6Quote(params.inputMint, params.outputMint, amount, params.slippageBps);
                    }
                    throw new Error(`HTTP Error: ${alternativeResponse.status} - ${alternativeText}`);
                }

                console.log("Alternative route found with higher slippage");
                return JSON.parse(alternativeText);
            }

            throw new Error(`HTTP Error: ${response.status} - ${responseText}`);
        }

        // Parse the JSON response
        const quoteData = JSON.parse(responseText);
        console.log('Quote obtained successfully');
        console.log('Raw quote result:', quoteData);

        // Log a comparison between what we sent and what Jupiter processed
        if (quoteData.inputAmount) {
            console.log(`🧪 Comparison: We sent ${amount}, Jupiter processed as ${quoteData.inputAmount}`);
        }

        return quoteData;
    } catch (error) {
        console.error('Error getting Jupiter quote:', error);
        throw error;
    }
}

/**
 * Try to get a quote using Jupiter V6 API (alternative endpoint)
 */
async function getJupiterV6Quote(inputMint: string, outputMint: string, amount: string, slippageBps?: string): Promise<any> {
    try {
        console.log("Attempting to use Jupiter V6 API for quote");

        // V6 API has a different format
        const requestBody = {
            inputMint,
            outputMint,
            amount,
            slippageBps: parseInt(slippageBps || '100'),
            forceFetch: true,
            onlyDirectRoutes: false,
            maxAccounts: 10,
        };

        const response = await fetch("https://quote-api.jup.ag/v6/quote", {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(requestBody)
        });

        console.log('V6 Quote response status:', response.status);

        // Handle 429 Too Many Requests error by falling back to Shyft proxy
        if (response.status === 429) {
            console.log('Received 429 Too Many Requests from Jupiter V6 API, falling back to shyft.ams endpoint');

            // Convert request for Shyft compatibility
            const queryParams = new URLSearchParams({
                inputMint,
                outputMint,
                amount,
                slippageBps: slippageBps || '100',
                onlyDirectRoutes: 'false',
                restrictIntermediateTokens: 'false'
            });

            const shyftUrl = `${JUPITER_API_BASE}${JUPITER_QUOTE_ENDPOINT}?${queryParams.toString()}`;
            console.log('Fallback V6 Quote URL:', shyftUrl);

            const shyftResponse = await fetch(shyftUrl, {
                headers: {
                    'Accept': 'application/json',
                    'x-api-key': SHYFT_API_KEY
                }
            });

            const shyftResponseText = await shyftResponse.text();

            if (!shyftResponse.ok) {
                throw new Error(`Shyft fallback also failed: ${shyftResponse.status} - ${shyftResponseText}`);
            }

            const shyftQuoteData = JSON.parse(shyftResponseText);
            console.log('V6 Quote obtained successfully from Shyft fallback');
            return shyftQuoteData;
        }

        const responseText = await response.text();

        if (!response.ok) {
            console.error(`Jupiter V6 API error: ${response.status} - ${responseText}`);
            throw new Error(`No route available for this token. The token might not have enough liquidity.`);
        }

        const v6Data = JSON.parse(responseText);

        // Convert V6 response to V1 format for compatibility
        return {
            inputAmount: v6Data.inputAmount,
            outputAmount: v6Data.outputAmount,
            otherAmountThreshold: v6Data.otherAmountThreshold,
            swapMode: "ExactIn",
            priceImpactPct: v6Data.priceImpactPct,
            routePlan: v6Data.routePlan,
            platformFee: v6Data.platformFee,
            inAmount: v6Data.inAmount,
            outAmount: v6Data.outAmount,
            // Add any other needed fields
        };
    } catch (error) {
        console.error("Error getting Jupiter V6 quote:", error);
        throw error;
    }
}

/**
 * Get serialized transactions for a swap
 * @param params Swap parameters
 * @returns Serialized transaction
 */
export async function getJupiterSwapTransaction(params: SwapParams): Promise<{ swapTransaction: string }> {
    try {
        console.log('Getting swap transaction for public key:', params.userPublicKey);

        // Include platform fee parameters if provided
        if (params.platformFeeBps && params.platformFeeAccount) {
            console.log(`Including platform fee: ${params.platformFeeBps} bps to ${params.platformFeeAccount}`);
        }

        // Prepare request body with all parameters
        const requestBody: any = {
            quoteResponse: params.quoteResponse,
            userPublicKey: params.userPublicKey,
            wrapAndUnwrapSol: params.wrapAndUnwrapSol ?? true
        };

        // Add platform fee if specified
        if (params.platformFeeBps && params.platformFeeAccount) {
            requestBody.platformFeeBps = params.platformFeeBps;
            requestBody.platformFeeAccount = params.platformFeeAccount;
        }

        // Add compute unit price if specified
        if (params.computeUnitPriceMicroLamports) {
            requestBody.computeUnitPriceMicroLamports = params.computeUnitPriceMicroLamports;
        }

        // Add optimization parameters for better transaction landing
        requestBody.dynamicComputeUnitLimit = true;

        // Only add dynamicSlippage if slippageBps is not already set
        // This ensures consistency with the getJupiterQuote function
        if (!params.quoteResponse.slippageBps) {
            requestBody.dynamicSlippage = true;
        }

        // Add priority fee estimation
        requestBody.prioritizationFeeLamports = {
            priorityLevelWithMaxLamports: {
                maxLamports: 1000000,
                priorityLevel: "veryHigh"
            }
        };

        const response = await fetch(JUPITER_SWAP_API, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(requestBody)
        });

        console.log('Swap transaction response status:', response.status);

        // Handle 429 Too Many Requests error by falling back to Shyft proxy
        if (response.status === 429) {
            console.log('Received 429 Too Many Requests from Jupiter API, falling back to shyft.ams endpoint');
            // Use Shyft endpoint for swap
            const shyftResponse = await fetch(`${JUPITER_API_BASE}${JUPITER_SWAP_ENDPOINT}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': SHYFT_API_KEY
                },
                body: JSON.stringify(requestBody)
            });

            const shyftResponseText = await shyftResponse.text();

            if (!shyftResponse.ok) {
                throw new Error(`Shyft fallback also failed: ${shyftResponse.status} - ${shyftResponseText}`);
            }

            const shyftSwapData = JSON.parse(shyftResponseText);
            console.log('Swap transaction obtained successfully from Shyft fallback');
            return shyftSwapData;
        }

        // Get the response text for better error reporting
        const responseText = await response.text();

        if (!response.ok) {
            // Check if it's a token program error
            if (responseText.includes("Missing token program")) {
                throw new Error(`Token not found in Jupiter. This may be a very new token that isn't indexed yet, or an invalid token address. Error: ${responseText}`);
            }

            // Check for TOKEN_NOT_TRADABLE error
            if (responseText.includes("TOKEN_NOT_TRADABLE")) {
                throw new Error(`TOKEN_NOT_TRADABLE: This token cannot be traded on Jupiter at this time. It may be too new, have low liquidity, or be restricted from trading.`);
            }

            throw new Error(`HTTP Error: ${response.status} - ${responseText}`);
        }

        // Parse the JSON response
        const swapData = JSON.parse(responseText);
        console.log('Swap transaction obtained successfully');
        return swapData;
    } catch (error) {
        console.error('Error getting Jupiter swap transaction:', error);
        throw error;
    }
}

/**
 * Execute a swap using the user's active trading wallet
 * @param swapTransaction Base64 encoded transaction
 * @param userWalletAddress User's connected wallet address
 * @param activeTradingWalletPublicKey Active trading wallet public key
 * @returns Transaction signature
 */
export async function executeJupiterSwap(
    swapTransaction: string,
    userWalletAddress: string,
    activeTradingWalletPublicKey: string
): Promise<{ signature: string }> {
    try {
        console.log(`Executing swap with active trading wallet: ${activeTradingWalletPublicKey}`);

        // Check the wallet balance first
        const balance = await checkWalletBalance(activeTradingWalletPublicKey);
        console.log(`Trading wallet balance: ${balance / LAMPORTS_PER_SOL} SOL`);

        // Minimum balance needed for most transactions (0.01 SOL)
        const MIN_BALANCE = 0.01 * LAMPORTS_PER_SOL;

        if (balance < MIN_BALANCE) {
        }

        // Restore the keypair from our trading wallet system
        const keypair = await restoreTradingWalletKeypair(userWalletAddress, activeTradingWalletPublicKey);

        if (!keypair) {
            throw new Error(`Could not restore keypair for trading wallet: ${activeTradingWalletPublicKey}`);
        }

        // Defensive check for Buffer availability before calling from()
        if (!safeBuffer || typeof safeBuffer.from !== 'function') {
            throw new Error("Buffer.from is not available. This is required for transaction processing.");
        }

        try {
            // Deserialize the transaction with defensive Buffer usage
            const transactionBuffer = safeBuffer.from(swapTransaction, 'base64');
            const transaction = VersionedTransaction.deserialize(transactionBuffer);

            // Add additional buffer for transaction fees (increased from previous value)
            const TRANSACTION_FEE_BUFFER = 0.001 * LAMPORTS_PER_SOL; // 0.001 SOL buffer for transaction fees

            try {
                // Simulate the transaction to get the exact fee cost
                const simulation = await connection.simulateTransaction(transaction);
                if (simulation.value.err) {
                    // If there's an insufficient funds error in the simulation, provide a clearer message
                    const errorMessage = JSON.stringify(simulation.value.err);
                    if (errorMessage.includes("insufficient funds") || errorMessage.includes("insufficient lamports")) {
                        throw new Error(`Insufficient funds: This transaction may require more SOL than available in your trading wallet.`);
                    } else {
                        throw new Error(`Transaction simulation failed: ${errorMessage}`);
                    }
                }
            } catch (simError) {
                // If simulation fails, still try to execute with the buffer
                console.warn("Transaction simulation failed, proceeding with execution:", simError);
            }

            // Sign the transaction with the trading wallet keypair
            transaction.sign([keypair]);

            // Send the transaction
            console.log('Sending transaction to Solana network...');
            try {
                const signature = await connection.sendRawTransaction(transaction.serialize());
                console.log('Transaction sent successfully:', signature);

                // CRITICAL FIX: Wait for transaction confirmation before returning success
                // This ensures sniper bot only counts actually confirmed transactions as successful
                console.log('Waiting for transaction confirmation...');
                try {
                    // Add timeout to prevent hanging indefinitely (30 seconds)
                    const confirmationPromise = connection.confirmTransaction(signature, 'confirmed');
                    const timeoutPromise = new Promise((_, reject) =>
                        setTimeout(() => reject(new Error('Transaction confirmation timeout after 30 seconds')), 30000)
                    );

                    const confirmation = await Promise.race([confirmationPromise, timeoutPromise]) as any;

                    if (confirmation.value.err) {
                        // Transaction failed on-chain
                        const errorMessage = JSON.stringify(confirmation.value.err);
                        console.error('Transaction failed on-chain:', errorMessage);
                        throw new Error(`Transaction failed on-chain: ${errorMessage}`);
                    }

                    console.log('Transaction confirmed successfully:', signature);
                } catch (confirmError) {
                    console.error('Transaction confirmation failed:', confirmError);

                    // Provide more specific error messages
                    if (confirmError instanceof Error) {
                        if (confirmError.message.includes('timeout')) {
                            throw new Error(`Transaction confirmation timeout: The transaction may still be processing. Signature: ${signature}`);
                        } else if (confirmError.message.includes('failed on-chain')) {
                            throw confirmError; // Re-throw on-chain failures as-is
                        } else {
                            throw new Error(`Transaction confirmation failed: ${confirmError.message}`);
                        }
                    } else {
                        throw new Error(`Transaction confirmation failed: Unknown confirmation error`);
                    }
                }

                // Trigger immediate balance refresh for this wallet
                window.dispatchEvent(new CustomEvent('ekko:wallet_selected', {
                    detail: {
                        walletPublicKey: activeTradingWalletPublicKey
                    }
                }));

                return { signature };
            } catch (txError: any) {
                // Handle specific Solana transaction errors
                if (txError.message && txError.message.includes("insufficient lamports")) {
                    const matchAmount = txError.message.match(/insufficient lamports (\d+), need (\d+)/);
                    if (matchAmount && matchAmount.length >= 3) {
                        const have = parseInt(matchAmount[1]) / LAMPORTS_PER_SOL;
                        const need = parseInt(matchAmount[2]) / LAMPORTS_PER_SOL;

                        // Add extra info to make it clearer what's happening
                        throw new Error(`Insufficient funds: Your trading wallet has ${have.toFixed(5)} SOL but needs ${need.toFixed(5)} SOL for this transaction (including network fees). Please fund your trading wallet with more SOL.`);
                    }
                }
                throw txError;
            }
        } catch (error) {
            throw error;
        }
    } catch (error) {

        throw error;
    }
}

/**
 * Complete swap flow from quote to execution for buying a token with SOL
 * @param outputMint The token mint address to buy
 * @param amountInSol Amount in SOL to swap
 * @param slippagePercent Slippage percentage (e.g. 2.5 for 2.5%)
 * @param userWalletAddress User's connected wallet address
 * @param activeTradingWalletPublicKey Active trading wallet public key
 * @returns Transaction signature
 */
export async function buySolToken(
    outputMint: string,
    amountInSol: string,
    slippagePercent: string,
    userWalletAddress: string,
    activeTradingWalletPublicKey: string
): Promise<{ signature: string }> {
    try {
        console.log(`Initiating swap: ${amountInSol} SOL → Token ${outputMint}`);
        console.log(`Using active trading wallet: ${activeTradingWalletPublicKey}`);

        // Extra debug info to help diagnose Buffer issues
        console.log('Buffer availability check:', {
            windowBuffer: typeof window !== 'undefined' ? typeof window.Buffer : 'window undefined',
            importedBuffer: typeof BufferImport,
            safeBuffer: typeof safeBuffer,
            hasFrom: safeBuffer ? typeof safeBuffer.from : 'no safeBuffer'
        });

        // Platform fee calculation - temporarily disabled (0%)
        const FEE_PERCENT = 0; // Temporarily set to 0%
        const FEE_DESTINATION = "2c2tkiuB7MpoZPvN5M9T6KtakWP9CajgjYLg5jDGHHTD";

        const totalSolAmount = parseFloat(amountInSol);
        const feeAmountInSol = totalSolAmount * FEE_PERCENT;
        const swapAmountInSol = totalSolAmount - feeAmountInSol;

        // Check the wallet balance FIRST before proceeding - with multiple checks to ensure accuracy
        // First check - immediate check
        let currentBalance = await checkWalletBalance(activeTradingWalletPublicKey);
        let currentBalanceInSol = currentBalance / LAMPORTS_PER_SOL;

        // Add a buffer for transaction fees (increased to 0.002 SOL to be safer)
        const TRANSACTION_FEE_BUFFER = 0.002;
        const totalRequiredSol = totalSolAmount + TRANSACTION_FEE_BUFFER;

        console.log(`Checking balance: Have ${currentBalanceInSol.toFixed(6)} SOL, need ${totalRequiredSol.toFixed(6)} SOL`);

        // Second check with a forced refresh - wait a moment and check again to get the most updated balance
        await new Promise(resolve => setTimeout(resolve, 500));
        currentBalance = await checkWalletBalance(activeTradingWalletPublicKey);
        currentBalanceInSol = currentBalance / LAMPORTS_PER_SOL;

        console.log(`Confirmed balance: ${currentBalanceInSol.toFixed(6)} SOL`);

        // Verify we have enough balance before continuing
        if (currentBalanceInSol < totalRequiredSol) {
            throw new Error(`Insufficient funds: Your trading wallet has ${currentBalanceInSol.toFixed(5)} SOL but needs ${totalRequiredSol.toFixed(5)} SOL for this transaction (including network fees). Please fund your trading wallet with more SOL.`);
        }

        // Calculate remaining SOL balance after this purchase
        const remainingSolBalance = currentBalanceInSol - totalRequiredSol;
        console.log(`Expected remaining SOL balance after purchase: ${remainingSolBalance.toFixed(6)}`);

        console.log(`Platform fee: ${feeAmountInSol.toFixed(9)} SOL (${FEE_PERCENT * 100}%) to ${FEE_DESTINATION}`);
        console.log(`Amount for swap: ${swapAmountInSol.toFixed(9)} SOL`);

        // FIXED: SOL has a fixed 9 decimals - correctly convert to lamports
        console.log(`[FIXED] SOL has ${SOL_DECIMALS} decimals (fixed at 9)`);
        const swapAmountInLamports = toAtomicUnits(swapAmountInSol, SOL_DECIMALS);
        const feeAmountInLamports = toAtomicUnits(feeAmountInSol, SOL_DECIMALS);

        console.log(`ATOMIC CONVERSION: ${swapAmountInSol} SOL → ${swapAmountInLamports} lamports`);

        // Verification calculation
        const verificationAmount = new Decimal(swapAmountInSol).mul(new Decimal(10).pow(SOL_DECIMALS)).toString();
        console.log(`VERIFICATION: ${swapAmountInSol} × 10^${SOL_DECIMALS} = ${verificationAmount}`);

        // If our atomic units don't match verification, something is wrong
        if (swapAmountInLamports !== verificationAmount) {
            console.error(`ERROR: Atomic conversion mismatch. Expected ${verificationAmount}, got ${swapAmountInLamports}`);
        }

        // Convert slippage percentage to basis points (1% = 100 bps)
        const slippageBps = (parseFloat(slippagePercent) * 100).toString();

        // 1. Get quote for the swap amount (not fee)
        console.log('Getting Jupiter quote...');
        console.log(`Getting quote with ATOMIC units: ${swapAmountInLamports}`);

        const quoteResponse = await getJupiterQuote({
            inputMint: SOL_MINT,
            outputMint,
            amount: swapAmountInLamports,
            slippageBps
        });
        console.log('Quote received successfully');

        // Get the estimated token output
        console.log(`Raw quote output amount: ${quoteResponse.outAmount}`);

        // CRITICAL: For output tokens, we need to fetch their decimals too
        // This is important for properly displaying the estimated output amount
        let outputTokenDecimals: number;
        try {
            // Get token decimals directly from blockchain
            const outputMintInfo = await getMint(connection, new PublicKey(outputMint));
            outputTokenDecimals = outputMintInfo.decimals;
            console.log(`[FIXED] Output token ${outputMint} has ${outputTokenDecimals} decimals (from blockchain)`);
        } catch (error) {
            console.warn('Could not get output token decimals:', error);
            // Default to 9 as fallback - better than assuming 6
            outputTokenDecimals = 9;
        }

        // Convert atomic output to human-readable amount
        const estimatedOutputAmount = fromAtomicUnits(quoteResponse.outAmount, outputTokenDecimals);
        console.log(`Estimated output: ${estimatedOutputAmount} tokens (using ${outputTokenDecimals} decimals)`);

        // 2. Get swap transaction
        console.log('Getting swap transaction...');
        const { swapTransaction } = await getJupiterSwapTransaction({
            quoteResponse,
            userPublicKey: activeTradingWalletPublicKey,
            wrapAndUnwrapSol: true
        });
        console.log('Swap transaction received');

        // Final balance check right before executing the transaction
        currentBalance = await checkWalletBalance(activeTradingWalletPublicKey);
        currentBalanceInSol = currentBalance / LAMPORTS_PER_SOL;

        if (currentBalanceInSol < totalRequiredSol) {
            throw new Error(`Balance check failed right before execution: Your trading wallet has ${currentBalanceInSol.toFixed(5)} SOL but needs ${totalRequiredSol.toFixed(5)} SOL.`);
        }

        // Immediately update UI with optimistic balance updates before the transaction
        // This provides instant feedback to the user that something is happening
        window.dispatchEvent(new CustomEvent('ekko:sol_balance_update_pending', {
            detail: {
                address: activeTradingWalletPublicKey,
                pendingAmount: `-${totalSolAmount}`, // Negative amount to indicate decrease
                pendingPurchase: true
            }
        }));

        // 3. Execute swap directly with our trading wallet
        console.log('Executing swap transaction...');
        const { signature } = await executeJupiterSwap(
            swapTransaction,
            userWalletAddress,
            activeTradingWalletPublicKey
        );
        console.log('Swap transaction executed successfully with signature:', signature);

        // Update the UI with new balances immediately after transaction is sent
        // First, update SOL balance
        window.dispatchEvent(new CustomEvent('ekko:sol_balance_update', {
            detail: {
                address: activeTradingWalletPublicKey,
                // Calculate new balance by subtracting total amount used
                estimatedBalance: (currentBalanceInSol - totalSolAmount).toString(),
                forceRefresh: true,
                source: 'token_purchase_completed'
            }
        }));

        // Then, update token balance with estimated output amount
        // Check if we already have this token (in case of a partial purchase)
        let existingTokenBalance = '0';
        try {
            existingTokenBalance = await getTokenBalance(activeTradingWalletPublicKey, outputMint);
            console.log(`Existing token balance before purchase: ${existingTokenBalance}`);
        } catch (error) {
            console.log('No existing balance found for this token, assuming first purchase');
        }

        // Calculate new token balance after purchase
        const existingTokenNum = parseFloat(existingTokenBalance) || 0;
        const estimatedTokenNum = parseFloat(estimatedOutputAmount);
        const newEstimatedTokenBalance = (existingTokenNum + estimatedTokenNum).toString();

        // Update token balance
        window.dispatchEvent(new CustomEvent('ekko:token_balance_update', {
            detail: {
                address: activeTradingWalletPublicKey,
                mint: outputMint,
                balance: newEstimatedTokenBalance,
                forceRefresh: true,
                source: 'token_purchase_completed'
            }
        }));

        // Also dispatch a general wallet refresh event
        window.dispatchEvent(new CustomEvent('ekko:wallet_selected', {
            detail: {
                walletPublicKey: activeTradingWalletPublicKey
            }
        }));

        // Emit a trade completed event with details
        window.dispatchEvent(new CustomEvent('ekko:trade_completed', {
            detail: {
                type: 'buy',
                token: { mint: outputMint },
                amount: amountInSol,
                estimatedTokensReceived: estimatedOutputAmount,
                walletAddress: activeTradingWalletPublicKey,
                timestamp: Date.now(),
                signature
            }
        }));

        // Platform fees completely removed for maximum speed

        console.log(`Swap (${swapAmountInSol.toFixed(9)} SOL → ${outputMint}) completed with signature ${signature}`);
        console.log(`Platform fee (${feeAmountInSol.toFixed(9)} SOL) will be sent to ${FEE_DESTINATION} in the background`);

        // Return the swap signature immediately
        return { signature };
    } catch (error) {
        // Provide more context about where the error occurred
        console.error('Error buying token with SOL:', error);

        // If there was an error, notify UI to reset any pending status
        window.dispatchEvent(new CustomEvent('ekko:sol_balance_update_failed', {
            detail: {
                error: error instanceof Error ? error.message : 'Unknown error during purchase',
                source: 'token_purchase_failed'
            }
        }));

        throw error;
    }
}

/**
 * Sells tokens for SOL
 * @param inputMint The token mint address to sell
 * @param amount The amount of token to sell in human-readable format (e.g. "29934.307678784")
 * @param slippagePercent The slippage percentage (default 1%)
 * @param userWalletAddress User's main wallet address
 * @param activeTradingWalletPublicKey Active trading wallet public key
 * @returns Transaction signature
 */
export async function sellTokenForSol(
    inputMint: string,
    amount: string,
    slippagePercent: string,
    userWalletAddress: string,
    activeTradingWalletPublicKey: string
): Promise<{ signature: string }> {
    try {
        console.log(`Initiating swap: ${amount} ${inputMint} → SOL`);
        console.log(`Using active trading wallet: ${activeTradingWalletPublicKey}`);

        // Platform fee calculation - temporarily disabled (0%)
        const FEE_PERCENT = 0; // Temporarily set to 0%
        const FEE_DESTINATION = "2c2tkiuB7MpoZPvN5M9T6KtakWP9CajgjYLg5jDGHHTD";

        // Check the token balance first
        const tokenBalance = await getTokenBalance(activeTradingWalletPublicKey, inputMint);
        const tokenBalanceDecimal = new Decimal(tokenBalance);
        const amountDecimal = new Decimal(amount);

        console.log(`Current token balance: ${tokenBalance} tokens`);
        console.log(`Attempting to sell: ${amount} tokens`);

        // Verify we have enough balance using Decimal.js for precise comparison
        // Add a small tolerance for floating point precision issues (1e-9)
        const tolerance = new Decimal('0.000000001');
        if (tokenBalanceDecimal.plus(tolerance).lessThan(amountDecimal)) {
            throw new Error(`Insufficient token balance: Your trading wallet has ${tokenBalance} tokens but needs ${amount} tokens for this transaction.`);
        }

        // Calculate the remaining balance after this sale
        const remainingTokenBalance = tokenBalanceDecimal.minus(amountDecimal).toNumber();
        console.log(`Expected remaining balance after sale: ${remainingTokenBalance}`);

        // Convert slippage percentage to basis points (1% = 100 bps)
        const slippageBps = (parseFloat(slippagePercent) * 100).toString();

        // CRITICAL FIX: Get the ACTUAL token decimals directly from the blockchain
        // This is the most reliable way to get the correct decimals
        let tokenDecimals: number;
        try {
            // Get token decimals directly from the blockchain using getMint
            const mintInfo = await getMint(connection, new PublicKey(inputMint));
            tokenDecimals = mintInfo.decimals;
            console.log(`[FIXED] Token ${inputMint} has ${tokenDecimals} decimals (from blockchain)`);
        } catch (error) {
            console.error('Error fetching token decimals from blockchain:', error);

            // Fallback to try getting from token info service
            try {
                const tokenInfo = await getTokenInfo(inputMint);
                if (tokenInfo && tokenInfo.status === 'success' && tokenInfo.data?.token?.decimals !== undefined) {
                    tokenDecimals = tokenInfo.data.token.decimals;
                    console.log(`[FALLBACK] Token ${inputMint} has ${tokenDecimals} decimals (from token info)`);
                } else {
                    // Last fallback - use 9 decimals which is common for most tokens
                    tokenDecimals = 9;
                    console.warn(`[WARNING] Could not determine token decimals, using default: ${tokenDecimals}`);
                }
            } catch (fallbackError) {
                // Ultimate fallback
                tokenDecimals = 9;
                console.warn(`[WARNING] All decimal fetching failed, using default: ${tokenDecimals}`);
            }
        }

        console.log(`Token decimals for ${inputMint}: ${tokenDecimals}`);
        console.log(`Human-readable amount to sell: ${amount}`);

        // ***********************************************************************
        // ACCURATE CONVERSION: Convert human amount to atomic units with the correct decimals
        // ***********************************************************************

        // Use Decimal.js for precise conversion to atomic units
        const amountInAtomicUnits = toAtomicUnits(amount, tokenDecimals);
        console.log(`ATOMIC CONVERSION: ${amount} → ${amountInAtomicUnits} (${tokenDecimals} decimals)`);

        // Double-check our conversion calculation
        const verificationAmount = new Decimal(amount).mul(new Decimal(10).pow(tokenDecimals)).toString();
        console.log(`VERIFICATION: ${amount} × 10^${tokenDecimals} = ${verificationAmount}`);

        // FIXED: Use Decimal comparison instead of string comparison to handle floating point precision
        const atomicDecimal = new Decimal(amountInAtomicUnits);
        const verificationDecimal = new Decimal(verificationAmount);

        if (!atomicDecimal.equals(verificationDecimal)) {
            console.warn(`WARNING: Minor atomic conversion difference. Expected ${verificationAmount}, got ${amountInAtomicUnits}`);
            console.log(`This is likely due to floating-point precision and is usually safe to ignore if the difference is minimal.`);

            // Check if the difference is significant (more than 1 atomic unit)
            const difference = atomicDecimal.minus(verificationDecimal).abs();
            if (difference.greaterThan(1)) {
                console.error(`ERROR: Significant atomic conversion mismatch. Difference: ${difference.toString()} atomic units`);
                throw new Error(`Atomic conversion failed: difference of ${difference.toString()} atomic units is too large`);
            }
        }

        // Restore the trading wallet keypair
        const tradingWalletKeypair = await restoreTradingWalletKeypair(
            userWalletAddress,
            activeTradingWalletPublicKey
        );

        if (!tradingWalletKeypair) {
            throw new Error(`Could not restore keypair for trading wallet: ${activeTradingWalletPublicKey}`);
        }

        // Get quote for selling token to SOL
        console.log(`Getting quote with ATOMIC units: ${amountInAtomicUnits}`);
        const quoteResponse = await getJupiterQuote({
            inputMint: inputMint,
            outputMint: SOL_MINT,
            amount: amountInAtomicUnits,
            slippageBps
        });

        // Get the estimated SOL output
        const estimatedOutputInLamports = quoteResponse.outAmount;
        // FIXED: Correctly convert from lamports to SOL by DIVIDING by 1e9
        const estimatedOutputInSol = parseFloat(estimatedOutputInLamports) / LAMPORTS_PER_SOL;

        console.log(`Estimated output: ${estimatedOutputInSol.toFixed(9)} SOL`);
        console.log(`Raw lamports: ${estimatedOutputInLamports}, converted to SOL: ${estimatedOutputInSol}`);

        // Calculate platform fee
        const feeAmountInSol = estimatedOutputInSol * FEE_PERCENT;
        console.log(`Platform fee: ${feeAmountInSol.toFixed(9)} SOL (${FEE_PERCENT * 100}%) to ${FEE_DESTINATION}`);

        // Convert fee amount to lamports using our utility function
        const feeAmountInLamports = toAtomicUnits(feeAmountInSol, SOL_DECIMALS);
        console.log(`Fee amount in lamports: ${feeAmountInLamports}`);

        // Get swap transaction
        const swapParams: any = {
            quoteResponse,
            userPublicKey: activeTradingWalletPublicKey,
            wrapAndUnwrapSol: true
        };

        const { swapTransaction } = await getJupiterSwapTransaction(swapParams);

        // Immediately update UI with optimistic balance updates before the transaction
        // This provides instant feedback to the user that something is happening
        window.dispatchEvent(new CustomEvent('ekko:token_balance_update_pending', {
            detail: {
                address: activeTradingWalletPublicKey,
                mint: inputMint,
                pendingAmount: `-${amount}`, // Negative amount to indicate decrease
                pendingSale: true
            }
        }));

        // Execute the swap
        const { signature } = await executeJupiterSwap(
            swapTransaction,
            userWalletAddress,
            activeTradingWalletPublicKey
        );

        console.log(`Transaction completed with signature: ${signature}`);

        // Update the UI with new balances immediately after transaction is sent
        // (Don't wait for blockchain confirmation to update UI)

        // 1. Update token balance - use remaining balance or zero if sold all
        const newTokenBalance = remainingTokenBalance > 0
            ? remainingTokenBalance.toString()
            : '0';

        // 2. Update SOL balance - estimate based on the swap result
        const netSolIncrease = estimatedOutputInSol - feeAmountInSol;

        // 3. Dispatch events to update the UI
        // Update token balance
        window.dispatchEvent(new CustomEvent('ekko:token_balance_update', {
            detail: {
                address: activeTradingWalletPublicKey,
                mint: inputMint,
                balance: newTokenBalance,
                forceRefresh: true,
                source: 'token_sale_completed'
            }
        }));

        // Update SOL balance
        window.dispatchEvent(new CustomEvent('ekko:sol_balance_update', {
            detail: {
                address: activeTradingWalletPublicKey,
                estimatedIncrease: netSolIncrease.toString(), // Estimated increase in SOL 
                forceRefresh: true,
                source: 'token_sale_completed'
            }
        }));

        // Also dispatch a general wallet refresh event
        window.dispatchEvent(new CustomEvent('ekko:wallet_selected', {
            detail: {
                walletPublicKey: activeTradingWalletPublicKey
            }
        }));

        // Emit a trade completed event with details
        window.dispatchEvent(new CustomEvent('ekko:trade_completed', {
            detail: {
                type: 'sell',
                token: { mint: inputMint },
                amount: amount,
                remainingBalance: newTokenBalance,
                estimatedSolReceived: estimatedOutputInSol.toString(),
                walletAddress: activeTradingWalletPublicKey,
                timestamp: Date.now(),
                signature
            }
        }));

        // Platform fees completely removed for maximum speed

        return { signature };
    } catch (error) {
        console.error('Error selling token for SOL:', error);

        // If there was an error, notify UI to reset any pending status
        window.dispatchEvent(new CustomEvent('ekko:token_balance_update_failed', {
            detail: {
                error: error instanceof Error ? error.message : 'Unknown error during sale',
                source: 'token_sale_failed'
            }
        }));

        throw error;
    }
}

// Platform fee functionality completely removed for maximum speed

// Create a single service object to export for use by components
export const jupiterService = {
    // Quick buy token with SOL
    quickBuyToken: async ({
        tokenMint,
        amount,
        slippage = 1
    }: {
        tokenMint: string;
        amount: number;
        slippage?: number
    }) => {
        try {
            // Get user's connected wallet
            const userWalletAddress = window.solana?.publicKey?.toString() ||
                window.solflare?.publicKey?.toString();

            if (!userWalletAddress) {
                throw new Error('No wallet connected');
            }

            // Show initial toast
            const toastId = `buy-${tokenMint}-${Date.now()}`;
            window.dispatchEvent(new CustomEvent('ekko:show_toast', {
                detail: {
                    message: `Checking wallet and preferences...`,
                    type: 'loading',
                    id: toastId,
                    tokenMint
                }
            }));

            // Import the user profile service to get the active trading wallet
            const { getUserProfile } = await import('./userProfileService');

            // Get the user's profile to access the active trading wallet with timeout
            const profilePromise = getUserProfile(userWalletAddress);
            const timeoutPromise = new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Profile check timed out')), 5000)
            );

            const profile = await Promise.race([profilePromise, timeoutPromise]) as { active_trade_wallet?: string | null };

            // Make sure we have an active trading wallet
            if (!profile?.active_trade_wallet) {
                // Show error toast if no active trading wallet
                window.dispatchEvent(new CustomEvent('ekko:show_toast', {
                    detail: {
                        message: 'Please activate a trading wallet first',
                        type: 'error',
                        id: toastId,
                        tokenMint
                    }
                }));
                return {
                    success: false,
                    error: 'No active trading wallet'
                };
            }

            // Use the active trading wallet from the user's profile
            const tradingWalletAddress = profile.active_trade_wallet;

            // Update toast to show we're executing the swap
            window.dispatchEvent(new CustomEvent('ekko:update_toast', {
                detail: {
                    message: `Buying tokens with ${amount} SOL...`,
                    type: 'loading',
                    id: toastId,
                    tokenMint
                }
            }));

            // Execute the swap with timeout
            const swapPromise = buySolToken(
                tokenMint,
                amount.toString(),
                slippage.toString(),
                userWalletAddress,
                tradingWalletAddress
            );
            const swapTimeoutPromise = new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Swap execution timed out')), 30000)
            );

            const result = await Promise.race([swapPromise, swapTimeoutPromise]) as { signature: string };

            // Show success toast
            window.dispatchEvent(new CustomEvent('ekko:show_toast', {
                detail: {
                    message: `Successfully purchased tokens!`,
                    type: 'success',
                    id: toastId,
                    tokenMint
                }
            }));

            return {
                success: true,
                txId: result.signature
            };
        } catch (error) {
            console.error('Quick buy token error:', error);

            let errorMessage = error instanceof Error ? error.message : 'Unknown error during purchase';

            // Handle timeout errors
            if (errorMessage.includes('timed out')) {
                errorMessage = 'Operation timed out. Please try again.';
            }
            // Check for specific "Pool is completed" error (error code 0x177d or 6013)
            else if (errorMessage.includes('custom program error: 0x177d') ||
                errorMessage.includes('Error Code: PoolIsCompleted') ||
                errorMessage.includes('Pool is completed')) {
                errorMessage = "This token has completed its bonding curve phase. Please use a DEX like Raydium or Jupiter directly to trade this token.";
            }
            // Check for slippage exceeded errors
            else if (errorMessage.includes('0x1771')) {
                errorMessage = "Slippage tolerance exceeded. Try increasing slippage or using a smaller amount.";
            }
            // Check for insufficient balance errors
            else if (errorMessage.includes('insufficient') && errorMessage.includes('lamports')) {
                errorMessage = "Insufficient SOL balance for this transaction. Please fund your trading wallet with more SOL.";
            }
            // Check for TOKEN_NOT_TRADABLE error
            else if (errorMessage.includes('TOKEN_NOT_TRADABLE')) {
                errorMessage = "This token cannot be traded on Jupiter at this time. It may be too new, have low liquidity, or be restricted from trading.";
            }
            // Check for missing token metadata error
            else if (errorMessage.includes('TOKEN_METADATA_MISSING') || errorMessage.includes('missing token decimals')) {
                errorMessage = "This token is too new and Jupiter is still indexing its metadata. Please try again in a few minutes or use a different DEX.";
            }

            // Show error toast
            window.dispatchEvent(new CustomEvent('ekko:show_toast', {
                detail: {
                    message: `Error: ${errorMessage}`,
                    type: 'error',
                    id: `buy-${tokenMint}-${Date.now()}`,
                    tokenMint
                }
            }));

            // Also dispatch an event to reset any pending balance updates
            window.dispatchEvent(new CustomEvent('ekko:sol_balance_update_failed', {
                detail: {
                    mint: tokenMint,
                    error: errorMessage
                }
            }));

            return {
                success: false,
                error: errorMessage
            };
        }
    },

    // Sell token for SOL
    sellToken: async ({
        tokenMint,
        amount,
        slippage = 1
    }: {
        tokenMint: string;
        amount: number;
        slippage?: number
    }) => {
        try {
            // Get user's connected wallet
            const userWalletAddress = window.solana?.publicKey?.toString() ||
                window.solflare?.publicKey?.toString();

            if (!userWalletAddress) {
                throw new Error('No wallet connected');
            }

            // Show toast notification about the transaction starting
            window.dispatchEvent(new CustomEvent('ekko:show_toast', {
                detail: {
                    message: `Preparing to sell ${amount} tokens...`,
                    type: 'loading',
                    id: `sell-${tokenMint}-${Date.now()}`,
                    tokenMint
                }
            }));

            // Import the user profile service to get the active trading wallet
            const { getUserProfile } = await import('./userProfileService');

            // Get the user's profile to access the active trading wallet
            const profile = await getUserProfile(userWalletAddress) as { active_trade_wallet?: string | null };

            // Make sure we have an active trading wallet
            if (!profile?.active_trade_wallet) {
                // Show error toast if no active trading wallet
                window.dispatchEvent(new CustomEvent('ekko:show_toast', {
                    detail: {
                        message: 'Please activate a trading wallet first',
                        type: 'error',
                        id: `sell-${tokenMint}-${Date.now()}`,
                        tokenMint
                    }
                }));
                return {
                    success: false,
                    error: 'No active trading wallet'
                };
            }

            // Use the active trading wallet from the user's profile
            const tradingWalletAddress = profile.active_trade_wallet;

            // Update toast to show we're executing the swap
            window.dispatchEvent(new CustomEvent('ekko:update_toast', {
                detail: {
                    message: `Selling ${amount} tokens...`,
                    type: 'loading',
                    id: `sell-${tokenMint}-${Date.now()}`,
                    tokenMint
                }
            }));

            // Execute the swap
            const result = await sellTokenForSol(
                tokenMint,
                amount.toString(),
                slippage.toString(),
                userWalletAddress,
                tradingWalletAddress
            );

            // Show success toast
            window.dispatchEvent(new CustomEvent('ekko:show_toast', {
                detail: {
                    message: `Successfully sold tokens!`,
                    type: 'success',
                    id: `sell-${tokenMint}-${Date.now()}`,
                    tokenMint
                }
            }));

            // Note: We don't need to dispatch balance update events here anymore
            // because the sellTokenForSol function now does that directly

            return {
                success: true,
                txId: result.signature
            };
        } catch (error) {
            console.error('Sell token error:', error);

            let errorMessage = error instanceof Error ? error.message : 'Unknown error during sale';

            // Check for specific "Pool is completed" error (error code 0x177d or 6013)
            if (errorMessage.includes('custom program error: 0x177d') ||
                errorMessage.includes('Error Code: PoolIsCompleted') ||
                errorMessage.includes('Pool is completed')) {

                errorMessage = "This token has completed its bonding curve phase. Please use a DEX like Raydium or Jupiter directly to trade this token.";
            }
            // Check for slippage exceeded errors
            else if (errorMessage.includes('0x1771')) {
                errorMessage = "Slippage tolerance exceeded. Try increasing slippage or using a smaller amount.";
            }
            // Check for insufficient token balance errors
            else if (errorMessage.includes('insufficient') &&
                (errorMessage.includes('lamports') || errorMessage.includes('balance'))) {
                errorMessage = "Insufficient token balance for this transaction.";
            }
            // Check for TOKEN_NOT_TRADABLE error
            else if (errorMessage.includes('TOKEN_NOT_TRADABLE')) {
                errorMessage = "This token cannot be traded on Jupiter at this time. It may be too new, have low liquidity, or be restricted from trading.";
            }
            // Check for missing token metadata error
            else if (errorMessage.includes('TOKEN_METADATA_MISSING') || errorMessage.includes('missing token decimals')) {
                errorMessage = "This token is too new and Jupiter is still indexing its metadata. Please try again in a few minutes or use a different DEX.";
            }

            // Show error toast
            window.dispatchEvent(new CustomEvent('ekko:show_toast', {
                detail: {
                    message: `Error: ${errorMessage}`,
                    type: 'error',
                    id: `sell-${tokenMint}-${Date.now()}`,
                    tokenMint
                }
            }));

            // Also dispatch an event to reset any pending balance updates
            window.dispatchEvent(new CustomEvent('ekko:token_balance_update_failed', {
                detail: {
                    mint: tokenMint,
                    error: errorMessage
                }
            }));

            return {
                success: false,
                error: errorMessage
            };
        }
    },

    // Expose direct methods for advanced usage
    getQuote: getJupiterQuote,
    getSwapTransaction: getJupiterSwapTransaction,
    executeSwap: executeJupiterSwap
};